import styled from "@emotion/styled";
import * as CSS from "csstype";
import React from "react";

type Direction = "horizontal" | "vertical";

const _Divider = styled("div")<Props>(
  ({
    border,
    color,
    direction,
    margin,
    minHeight,
    height,
    width,
    flex,
    theme,
  }) => ({
    ...(direction === "horizontal"
      ? {
          borderBottom: `${border ?? "1px"} solid ${
            color ?? theme.divider_color
          }`,
          marginBottom: margin ?? theme.space_lg,
          marginTop: margin ?? theme.space_lg,
          width: width ?? "100%",
        }
      : {
          borderLeft: `${border ?? "1px"} solid ${
            color ?? theme.divider_color
          }`,
          flex,
          marginLeft: margin ?? theme.space_lg,
          marginRight: margin ?? theme.space_lg,
          height: height ?? "100%",
          minHeight,
        }),
  })
);

interface Props {
  border?: CSS.Property.Border;
  color?: CSS.Property.Color;
  direction?: Direction;
  flex?: number | string;
  height?: number | string;
  margin?: CSS.Property.Margin;
  minHeight?: number | string;
  width?: number | string;
}

/**
 * Divider Component
 *
 * A flexible horizontal or vertical divider for layouts.
 *
 * ## Usage
 *
 * ### Horizontal Divider (default)
 * ```tsx
 * <Divider />
 * ```
 * - Fills the width of its parent by default (`width: 100%`).
 * - You can override the width with the `width` prop.
 *
 * ### Vertical Divider
 * ```tsx
 * <Divider direction="vertical" />
 * ```
 * - Fills the height of its parent by default (`height: 100%`).
 * - You can override the height with the `height` prop.
 *
 * ## Important Notes & CSS Gotchas
 * - **Parent Height Requirement (Vertical):**
 *   - For `height: 100%` to work, the parent element must have an explicit height (e.g., `height: 200px`, `height: 100%` with all ancestors also sized, or be inside a flex column with a set height).
 *   - If the parent does **not** have a set height, the divider will only be as tall as the parent's content, and may not appear as a full-height divider.
 *   - This is a fundamental CSS limitation: children cannot stretch to fill a parent with no defined height.
 *   - **Flexbox does not automatically make the parent fill available vertical space.** You must set a height on the parent or its ancestors.
 *
 * - **Parent Width Requirement (Horizontal):**
 *   - For `width: 100%` to work, the parent element must have an explicit width.
 *
 * - **Grid Layouts:**
 *   - In CSS Grid, the divider will fill the grid area if the parent row/column is sized.
 *
 * ## Props
 * @param {'horizontal' | 'vertical'} [direction='horizontal'] - Divider orientation.
 * @param {CSS.Property.Border} [border] - CSS border value.
 * @param {CSS.Property.Color} [color] - CSS color value.
 * @param {CSS.Property.Margin} [margin] - CSS margin value.
 * @param {number | string} [minHeight] - Minimum height (vertical only).
 * @param {number | string} [height] - Height (vertical only).
 * @param {number | string} [width] - Width (horizontal only).
 * @param {number | string} [flex] - Flex value for flexbox layouts (vertical only).
 *
 * ## Examples
 *
 * **Vertical divider in a flex row:**
 * ```tsx
 * <div style={{ display: "flex", height: 200 }}>
 *   <div>Left</div>
 *   <Divider direction="vertical" flex={1} />
 *   <div>Right</div>
 * </div>
 * ```
 *
 * **Vertical divider with explicit height:**
 * ```tsx
 * <Divider direction="vertical" height="150px" />
 * ```
 *
 * @component
 */

export default function Divider({
  direction = "horizontal",
  ...props
}: Props): React.JSX.Element {
  return <_Divider direction={direction} {...props} />;
}
